import { useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { Alert, AlertDescription, Button, Card, EmptyState, Skeleton } from "@ndd/ui";
import { AlertCircle, Info, Sparkles, X } from "lucide-react";
import React, { useCallback, useEffect, useReducer, useState } from "react";
import type {
  ScorecardWorkbenchBaselineResponse,
  ScorecardWorkbenchConfigResponse,
  ScorecardWorkbenchSessionSummary,
  ScorecardWorkbenchSessionsResponse,
} from "../../../../shared/scorecard-workbench";
import { Badge } from "../../components/badge";
import { SessionPicker } from "../../components/scorecard-workbench/session-picker";
import { BaselineSummaryCard } from "../../components/scorecard-workbench/summary-card";
import { fetchWithAuthRetry } from "../../lib/api";
import { summarizeBaseline } from "../../lib/scorecard-workbench-baseline";
import {
  canGenerate,
  createScorecardWorkbenchState,
  generationBlockedReason,
  scorecardWorkbenchReducer,
  summaryForTypedId,
} from "../../lib/scorecard-workbench-state";
import { AdminGuard, AdminNav } from "./shared";

const BASE = "/api/admin/scorecard-workbench";
type WorkbenchConfig = ScorecardWorkbenchConfigResponse["data"];

export function AdminScorecardWorkbench(): React.ReactElement {
  return (
    <AdminGuard>
      <ScorecardWorkbenchLoader />
    </AdminGuard>
  );
}

function PageFrame({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <main className="min-h-screen bg-background px-5 py-8 sm:px-8">
      <div className="mx-auto w-full max-w-[1440px]">
        <AdminNav />
        {children}
      </div>
    </main>
  );
}

function ScorecardWorkbenchLoader(): React.ReactElement {
  const { getToken } = useAuth();
  const [config, setConfig] = useState<WorkbenchConfig | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const response = await fetchWithAuthRetry(getToken, `${BASE}/config`, {
          cache: "no-store",
          signal: controller.signal,
        });

        if (!response.ok) {
          setUnavailable(true);

          return;
        }

        const body = (await response.json()) as ScorecardWorkbenchConfigResponse;
        setConfig(body.data);
      } catch {
        if (controller.signal.aborted) return;

        Sentry.captureException(
          new Error("Scorecard workbench configuration request failed"),
          { tags: { surface: "scorecard_workbench", source: "client" } }
        );
        setUnavailable(true);
      }
    })();

    return () => controller.abort();
  }, [getToken]);

  if (unavailable) {
    return (
      <PageFrame>
        <EmptyState
          icon={AlertCircle}
          title="Scorecard workbench unavailable"
          description="This surface is not enabled in this environment."
        />
      </PageFrame>
    );
  }

  if (!config) {
    return (
      <PageFrame>
        <div className="space-y-4" aria-label="Loading Scorecard workbench">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </PageFrame>
    );
  }

  return <ScorecardWorkbenchSurface config={config} />;
}

function PageHead({ onClear }: { onClear: () => void }): React.ReactElement {
  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div className="max-w-[560px]">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Clinical admin tool
          </p>
          <h1 className="mt-1 text-2xl font-semibold text-foreground">
            Scorecard Workbench
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Run the production Analysis prompt on another model and compare the
            candidate Scorecard with the one this Session already has.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onClear}>
          Clear all
        </Button>
      </div>
      <Alert variant="warning" className="mt-5">
        <AlertDescription>
          <strong className="font-semibold">Nothing here becomes a record.</strong>{" "}
          Leaving or refreshing this page clears the Session, every run, and every pin.
        </AlertDescription>
      </Alert>
    </>
  );
}

/**
 * The badge follows the loaded baseline, not the picked row: an id typed by
 * hand carries no Analysis status, and guessing one mislabels the Session.
 */
function SessionChip({
  session,
  analyzing,
  onClear,
}: {
  session: ScorecardWorkbenchSessionSummary;
  analyzing: boolean;
  onClear: () => void;
}): React.ReactElement {
  return (
    <div className="mb-4 inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5">
      <span className="font-mono text-body text-muted-foreground">
        {session.sessionId}
      </span>
      {session.clientName ? (
        <span className="text-sm font-semibold text-foreground">
          {session.clientName}
        </span>
      ) : null}
      {analyzing ? <Badge variant="analyzing">Analyzing</Badge> : null}
      <button type="button" aria-label="Clear the selected Session" onClick={onClear}>
        <X className="size-4 text-muted-foreground" />
      </button>
    </div>
  );
}

function ControlsColumn({
  analysisVersion,
  blockedReason,
}: {
  analysisVersion: string;
  blockedReason: string | null;
}): React.ReactElement {
  return (
    <div className="w-full shrink-0 lg:w-[340px]">
      <Card className="p-4">
        <p className="text-sm font-medium text-foreground">Prompt</p>
        <p className="mt-2 rounded-md border border-dashed border-(color:--border-decorative,var(--border-strong)) px-3 py-2 text-body text-muted-foreground">
          Production · <span className="font-mono">{analysisVersion}</span>
        </p>
        <Button variant="ai" className="mt-4 w-full" disabled={blockedReason !== null}>
          <Sparkles className="size-4" /> Generate candidate
        </Button>
      </Card>
      {blockedReason ? (
        <p className="mt-3 text-body text-muted-foreground">{blockedReason}</p>
      ) : null}
    </div>
  );
}

interface RecentSessions {
  sessions: ScorecardWorkbenchSessionSummary[];
  failed: boolean;
}

/** An unreadable list is reported, never rendered as "no recent Sessions". */
function useRecentSessions(reloadKey: number): RecentSessions {
  const { getToken } = useAuth();
  const [sessions, setSessions] = useState<ScorecardWorkbenchSessionSummary[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const response = await fetchWithAuthRetry(getToken, `${BASE}/sessions`, {
          cache: "no-store",
          signal: controller.signal,
        });

        if (!response.ok) {
          setFailed(true);

          return;
        }

        const body = (await response.json()) as ScorecardWorkbenchSessionsResponse;
        setSessions([...body.data.sessions]);
        setFailed(false);
      } catch {
        if (controller.signal.aborted) return;

        Sentry.captureException(
          new Error("Scorecard workbench recent Sessions request failed"),
          { tags: { surface: "scorecard_workbench", source: "client" } }
        );
        setFailed(true);
      }
    })();

    return () => controller.abort();
  }, [getToken, reloadKey]);

  return { sessions, failed };
}

function BaselineSlot({
  loading,
  error,
  notComplete,
  children,
}: {
  loading: boolean;
  error: string | null;
  notComplete: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  if (loading) return <Skeleton className="h-48 w-full" />;

  if (error) {
    return <EmptyState icon={AlertCircle} title="No baseline" description={error} />;
  }

  if (notComplete) {
    return (
      <EmptyState
        icon={Info}
        title="No baseline yet"
        description="This Session's Analysis is still running. Candidates need a completed Scorecard to compare against. Check back when it finishes, or pick another Session."
      />
    );
  }

  return <>{children}</>;
}

function baselineFailure(status: number): string {
  if (status === 404) return "That Session id was not found.";

  return "The baseline Scorecard could not be loaded.";
}

function modelLabelFor(config: WorkbenchConfig, modelKey: string): string {
  const descriptor = config.models.find((model) => model.key === modelKey);

  return descriptor ? descriptor.label : modelKey;
}

/** Reloading on a restored page keeps the back-forward cache from reviving PHI. */
function useReloadOnRestore(): void {
  useEffect(() => {
    const reloadOnRestore = (event: PageTransitionEvent): void => {
      if (event.persisted) window.location.reload();
    };

    window.addEventListener("pageshow", reloadOnRestore);

    return () => window.removeEventListener("pageshow", reloadOnRestore);
  }, []);
}

function useBaselineSelection(): {
  state: ReturnType<typeof createScorecardWorkbenchState>;
  select: (session: ScorecardWorkbenchSessionSummary) => void;
  clear: () => void;
} {
  const { getToken } = useAuth();
  const [state, dispatch] = useReducer(
    scorecardWorkbenchReducer,
    undefined,
    createScorecardWorkbenchState
  );

  const load = useCallback(
    async (session: ScorecardWorkbenchSessionSummary) => {
      const sessionId = session.sessionId;

      try {
        const response = await fetchWithAuthRetry(
          getToken,
          `${BASE}/sessions/${encodeURIComponent(sessionId)}/baseline`,
          { cache: "no-store" }
        );

        if (!response.ok) {
          dispatch({
            type: "baseline_failed",
            sessionId,
            message: baselineFailure(response.status),
          });

          return;
        }

        const body = (await response.json()) as ScorecardWorkbenchBaselineResponse;
        dispatch({ type: "baseline", sessionId, baseline: body.data });
      } catch {
        Sentry.captureException(
          new Error("Scorecard workbench baseline request failed"),
          { tags: { surface: "scorecard_workbench", source: "client" } }
        );
        dispatch({
          type: "baseline_failed",
          sessionId,
          message: "The baseline Scorecard could not be loaded.",
        });
      }
    },
    [getToken]
  );

  const select = useCallback(
    (session: ScorecardWorkbenchSessionSummary) => {
      dispatch({ type: "select", session });
      void load(session);
    },
    [load]
  );

  return { state, select, clear: () => dispatch({ type: "clear" }) };
}

function SelectedSession({
  state,
  config,
  onClear,
}: {
  state: ReturnType<typeof createScorecardWorkbenchState>;
  config: WorkbenchConfig;
  onClear: () => void;
}): React.ReactElement | null {
  if (!state.selected) return null;

  const summary = state.baseline ? summarizeBaseline(state.baseline) : null;

  return (
    <>
      <SessionChip
        session={state.selected}
        analyzing={state.baseline?.status === "not_complete"}
        onClear={onClear}
      />
      <BaselineSlot
        loading={state.loading}
        error={state.error}
        notComplete={state.baseline?.status === "not_complete"}
      >
        {summary ? (
          <BaselineSummaryCard
            summary={summary}
            modelLabel={modelLabelFor(config, summary.facts.modelKey)}
          />
        ) : null}
      </BaselineSlot>
    </>
  );
}

function ScorecardWorkbenchSurface({
  config,
}: {
  config: WorkbenchConfig;
}): React.ReactElement {
  const { state, select, clear } = useBaselineSelection();
  const [reloadKey, setReloadKey] = useState(0);
  const { sessions, failed } = useRecentSessions(reloadKey);
  useReloadOnRestore();

  return (
    <PageFrame>
      <PageHead onClear={clear} />
      <div className="mt-6 flex flex-col gap-6 lg:flex-row">
        <ControlsColumn
          analysisVersion={config.analysisVersion}
          blockedReason={canGenerate(state) ? null : generationBlockedReason(state)}
        />
        <div className="min-w-0 flex-1">
          {state.selected ? (
            <SelectedSession state={state} config={config} onClear={clear} />
          ) : (
            <SessionPicker
              sessions={sessions}
              listFailed={failed}
              onRetry={() => setReloadKey((key) => key + 1)}
              onSelect={select}
              onSubmitId={(sessionId) => select(summaryForTypedId(sessionId, sessions))}
            />
          )}
        </div>
      </div>
    </PageFrame>
  );
}
